import { Theme } from "theme-ui";
import { transparentize } from "polished";
import { SystemStyleObject } from "@styled-system/css";
import { htmlElementStyles } from "packages/dsaui/src/html";
import {
  alerts,
  text,
  links,
  buttons,
  cards,
  forms,
} from "packages/dsaui/src/componentStyles";

// Any variant exposed by theme-ui
type VariantKey =
  | "text"
  | "links"
  | "buttons"
  | "grids"
  | "images"
  | "cards"
  | "layout"
  | "forms"
  | "badges"
  | "alerts"
  | "messages";

interface VariantObject {
  [k: string]: SystemStyleObject;
}

// See https://theme-ui.com/components/variants
export type Variants = Partial<Record<VariantKey, VariantObject>>;

type LocalTheme = Theme & Variants;

export interface AppTheme extends LocalTheme {
  fontSizes: string[];
}

const klimaFont = `Klima, sans-serif`;

export const fontSizes: AppTheme["fontSizes"] = [
  "0.9em", // 0 - small
  "1em", // 1 - medium
  "1.25em", // 2 - large
  "1.5em", // 3 - xlarge
  "3em", // 4 - xxlarge
];

export const fonts: AppTheme["fonts"] = {
  body: klimaFont,
  heading: klimaFont,
  sans: klimaFont,
  mono: "courier",
};

export const fontWeights: AppTheme["fontWeights"] = {
  thin: 100,
  light: 300,
  normal: 400,
  body: 400,
  medium: 500,
  bold: 600,
  black: 700,
};

export const lineHeights: AppTheme["lineHeights"] = {
  body: 1.375,
  heading: 1.125,
};

// See https://howto-wordpress-tips.com/responsive-breakpoints-tutorial
export const deviceBreakpoints: { [k: string]: number } = {
  xs: 0, // Most phones in portrait
  sm: 420, // Most phones in landscape
  md: 768, // Most tablets in portrait
  lg: 1024, // Most tablets in landscape, older monitors
  xl: 1366, // HD, 1080P, 4K monitors
};

export const breakpoints: AppTheme["breakpoints"] = [
  // the xs (0) breakpoint is implied
  `${deviceBreakpoints.sm}px`,
  `${deviceBreakpoints.md}px`,
  `${deviceBreakpoints.lg}px`,
  `${deviceBreakpoints.xl}px`,
];

const colorPalette = {
  black: "#241f20",
  darkGray: "#747380",
  darkRed: "#9f0005",
  errorRed: "#C80000",
  lightGray: "#f6f6f6",
  midGray: "#e4e4e8",
  peach: "#ffe2d6",
  red: "#e70109",
  white: "#fff",
  green: "#00a02a",
  lightGreen: "#1fec54",
};

const themeColors = {
  background: colorPalette.white,
  text: colorPalette.black,
  primary: colorPalette.red,
  link: colorPalette.darkGray,
  message: { background: "#FFFBEB", text: "#92400E" },
  notifications: {
    error: colorPalette.red,
    warning: colorPalette.darkRed,
    info: colorPalette.peach,
    success: colorPalette.green,
  },
  overlay: transparentize(0.5, colorPalette.black),
};

export const colors: AppTheme["colors"] = {
  ...colorPalette,
  ...themeColors,
};

export const space: AppTheme["space"] = {
  // space scale for use in margin, padding, etc.
  ...[
    0,
    "0.25em", // 1 - xsmall
    "0.5em", // 2 - small
    "1em", // 3 - medium
    "1.5em", // 4 - large
    "2em", // 5 - xlarge
    "4em", // 6 - xlarge
    "6em", // 7 - xxlarge
  ],
  card: "600px",
  page: {
    desktop: "4em",
    mobile: "7em",
  },
};

export const radii: AppTheme["radii"] = [0, ".125em", ".25em", "0.5em"];

export const shadows: AppTheme["shadows"] = [
  "none",
  "inset 0 0 0 2px",
  "0 0 16px rgba(0, 0, 0, .25)",
];

export const theme: AppTheme = {
  breakpoints,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  radii,
  shadows,
  space,
  styles: htmlElementStyles,
  // TODO: Fix type casting
  buttons: buttons as any,
  cards: cards as any,
  forms: forms as any,
  links: links as any,
  text: text as any,
  alerts: alerts as any,
};

export default theme;
